import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDispatch } from 'react-redux';
import SatInput from '@src/components/common/satUI/SatInput';
import SatSelect from '@src/components/common/satUI/SatSelect';
import SatModal from '@components/common/satUI/SatModal';
import {
  saveSimulationInfo,
  calcSimulation,
} from '@src/store/actions/planAction';
import {
  isEmptyObject,
  makePremiumStandard,
  toCurrency,
  toNumber,
} from '@common/utils';
import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatRadio from '@components/common/satUI/SatRadio';
import {
  GridHeader,
  GridTable,
  GridInput,
  GridGuide,
} from '@components/common/grid';

const AdditionalPayment = forwardRef((props, ref) => {
  const {
    simulationInfo: {
      mainPlanInfo = {},
      additionalPaymentInfo = {},
      simulationList = [],
      totalPremium,
      calculationContent,
      mainProduct,
    },
    type,
  } = props;

  const inputRef = useRef();
  const dispatch = useDispatch();
  const [annuityTypeValueOptions, setAnnuityTypeValueOptions] = useState([]);
  const [annuityDisabled, setAnnuityDisabled] = useState(true);
  const [isValidCheck, setIsValidCheck] = useState(false);
  const [validMsg, setValidMsg] = useState(null);
  const [isSummary, setIsSummary] = useState(false); // 경과년수 요약보기 여부
  const [irregularValueMap, setIrregularValueMap] = useState({}); // 비정기 추가납입 금액

  useEffect(() => {
    setAnnuityDisabled(additionalPaymentInfo.annuityType === '0');
    //연금선지급기간 선택 옵션 값 설정
    if (additionalPaymentInfo.annuityType !== '0') {
      handleSelectAnnuityType(additionalPaymentInfo.annuityType, false);
    }
  }, [additionalPaymentInfo.annuityType, additionalPaymentInfo.monthlyPremium]);

  const checkCalcValid = () => {
    let result = true;
    return result;
  };
  /**
   *
   * @param {*} type     선택항목 타입
   * @returns
   */
  const getStepOptions = type => {
    let options = [];

    let startValue = additionalPaymentInfo.applyAge;
    let maxValue = additionalPaymentInfo.coveragePeriodValue;
    let step = 1;
    let initValue = 0;
    let unit = '세';
    const startAge = additionalPaymentInfo.annuityOpenAge;

    switch (type) {
      case 'startAdd': //추가납입시작나이
        if (additionalPaymentInfo.periodType === 'year') {
          initValue++;
          startValue++;
        }
        maxValue = 115;
        break;
      case 'endAdd': //추가납입 년수
        startValue = 1;
        maxValue =
          maxValue -
          (additionalPaymentInfo.applyAge + additionalPaymentInfo.startAddAge);
        unit = '년';
        initValue = 1;
        break;
      case 'annuityOpenAge':
        for (let i = startAge; i < 91; i++) {
          options.push({ label: `${i}세`, value: i });
        }
        return options;
    }

    for (let i = 0; i < maxValue - startValue + 1; i = i + step) {
      options.push({
        label: startValue + i + unit,
        value: initValue + i,
      });
    }

    return options;
  };

  const getPeriodType = () => {
    const periodList = [
      { label: '매월', value: 'month' },
      { label: '매년', value: 'year' },
    ];
    return periodList;
  };

  const checkValid = () => {
    //에러메시지 리셋
    setValidMsg('');
    setIsValidCheck(false);

    // 보험료 납부 기간
    const { payinPeriodTypeCode = '', payinPeriodValue = '' } = mainProduct;
    const payinPeriod =
      payinPeriodTypeCode === '1'
        ? +payinPeriodValue.substring(0, 2)
        : +payinPeriodValue.substring(0, 2) - +additionalPaymentInfo.applyAge;

    // 추가납입 한도 체크
    const payinType = additionalPaymentInfo.periodType;
    const startAge =
      +additionalPaymentInfo.applyAge +
      +additionalPaymentInfo.startAddAge +
      (payinType === 'month' ? 0 : 1);
    const payinPremium =
      +additionalPaymentInfo.monthlyPremium?.replace(/\D/g, '') || 0;
    const applyPayValue = +(payinType === 'month'
      ? payinPremium * 12
      : payinPremium);
    const addPaymentYear = +additionalPaymentInfo.endAddAge || 0; // 추가 납입 하는 년수

    const limitProp = 1;
    const targetSurrenderData =
      simulationList
        .find(item => item.simlNm === type)
        ?.calcData?.periodicDeathBenefitsDTO?.map(item => ({
          ...item.additionalPaymentResponseDTO,
          insrnAge: item.insrnAge,
        }))
        .map(item => ({
          ...item,
          progPdVal:
            item.progPd / 12 < 1
              ? `${item.progPd}개월`
              : `${item.progPd / 12}년`,
        }))
        .filter(item => !item.progPdVal?.includes('개월')) || [];
    const firstPremium = mainProduct.premium; // 주계약 보험료
    let insrnAge = null;
    let limitPayValue = 0;
    let accAddPayPremium = 0;
    for (let i = 0, size = targetSurrenderData.length; i < size; i++) {
      const rowId = i + 1;
      const item = targetSurrenderData[i];
      insrnAge = +item.insrnAge;
      // 정기 추가납입 기간 내
      if (
        insrnAge >= startAge &&
        insrnAge < startAge + addPaymentYear &&
        applyPayValue
      ) {
        accAddPayPremium += applyPayValue;
      }
      // 비정기 추가납입
      if (!isSummary || rowId % 5 === 0) {
        const irregularValue = +toNumber(irregularValueMap[rowId]);
        if (irregularValue) {
          accAddPayPremium += irregularValue;
        }
      }
      if (+toNumber(item.progPdVal) <= payinPeriod) {
        limitPayValue += firstPremium * limitProp * 12;
      }
      if (accAddPayPremium > limitPayValue) {
        break;
      }
    }

    if (accAddPayPremium > limitPayValue) {
      setValidMsg(
        '추가납입 보험료가 납입된보험료의 ' +
          limitProp * 100 +
          '%를 초과할 수 없습니다.\n' +
          insrnAge +
          '세 까지의 추가납입보험료 최대 가능금액: ' +
          toCurrency(limitPayValue) +
          '원\n' +
          '해당 시점까지 납입된 추가납입보험료 합계: ' +
          toCurrency(accAddPayPremium) +
          '원',
      );
      setIsValidCheck(true);
      // inputRef.current.onFocus();
      // changeSimulationCondition('monthlyPremium', '0');
      return false;
    }
    return true;
  };

  const handleSelectAnnuityType = (value, isChange) => {
    let startIdx = 0;
    let endIdx = -1;
    let step = 0;
    let unit = '';

    if (value === '1') {
      startIdx = 10;
      endIdx = 20;
      step = 5;
      unit = '년';
    } else if (value === '2') {
      startIdx = 1;
      endIdx = 10;
      step = 1;
      unit = '%';
    }

    let annuityTypeValueOptions = [];
    if (value === '0')
      annuityTypeValueOptions.push({ label: '선택', value: 0 });

    for (let i = startIdx; i <= endIdx; i += step) {
      annuityTypeValueOptions.push({ label: i + unit, value: i });
    }

    if (value === '1')
      annuityTypeValueOptions.push({ label: '100세', value: 100 });

    setAnnuityTypeValueOptions(annuityTypeValueOptions);

    if (isChange) changeSimulationCondition('annuityTypeValue', startIdx);
  };

  const changeSimulationCondition = (type, value) => {
    const tmpSimulList = [];
    Object.assign(tmpSimulList, simulationList);

    let resetObj = {};
    if (type === 'annuityType') handleSelectAnnuityType(value, true);
    else if (type === 'periodType') {
      resetObj = { startAddAge: value === 'month' ? 0 : 1, endAddAge: 1 };
    } else if (type === 'startAddAge') {
      resetObj = { endAddAge: 1 };
    }

    dispatch(
      saveSimulationInfo({
        simulationList: [...tmpSimulList],
        additionalPaymentInfo: {
          ...resetObj,
          [type]: value,
        },
      }),
    );
  };

  const handleCalcClick = () => {
    if (!checkValid()) {
      return;
    }
    // 정기 추가납입
    const addPayin = {
      addPayinStrtAge:
        +additionalPaymentInfo.applyAge + +additionalPaymentInfo.startAddAge,
      addPayinPd: additionalPaymentInfo.endAddAge,
      addPayinFrqcyCd: additionalPaymentInfo.periodType === 'year' ? '1' : '2',
      addPayinPrmum:
        additionalPaymentInfo.monthlyPremium?.replace(/\D/g, '') || '0',
    };
    // 연금선지급
    const anutyUpfrnt = {
      anutyUpfrntCd: additionalPaymentInfo.annuityType || '0', // 사망보험금 연금선지급 서비스
      anutyUpfrntVal: additionalPaymentInfo.annuityTypeValue || '',
      anutyUpfrntBgnAge: additionalPaymentInfo.annuityOpenAge || '',
    };
    // 연금선지급값 추가.
    let additionalPaymentObj = {};
    if (anutyUpfrnt.anutyUpfrntCd !== '0') {
      additionalPaymentObj = { ...anutyUpfrnt };
    }
    if (addPayin?.addPayinPd) {
      additionalPaymentObj = { ...additionalPaymentObj, ...addPayin };
    }
    const additionalPayment =
      Object.keys(additionalPaymentObj).length === 0
        ? []
        : [additionalPaymentObj];
    // 일시금추가납입 정보
    const additionalPaymentList = Object.keys(irregularValueMap).map(key => {
      return {
        lpsumAddPayinAge: additionalPaymentInfo.applyAge + +key,
        lpsumAddPayinPrmum: irregularValueMap[key],
      };
    });
    const payload = {
      simulType: 'calcAddPay',
      simlNm: type,
      prmumCalcBasDd: mainPlanInfo.calculateDate,
      joinDsgnSimlCd: type,
      saleProdCd: mainPlanInfo.saleProdCd,
      gndrCd: mainPlanInfo.genderCode,
      riskGrdCd: '3',
      insrnAge: mainPlanInfo.age,
      payinFrqcyCd: mainPlanInfo.payinFrqcyCd,
      planEstablishProductDTO: mainPlanInfo.planEstablishProductDTO,
      additionalPayment,
      additionalPaymentList,
      fundInfo: mainPlanInfo.fundInfo,
    };
    dispatch(calcSimulation(payload));
  };

  useImperativeHandle(ref, () => ({
    calc: () => handleCalcClick(),
    checkValid: () => checkCalcValid(),
  }));

  const MainPlanInfo = props => {
    const { mainPlanInfo, isUSD } = props;

    return (
      <>
        <table className="tbl-sty3 mgt-16">
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '31%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead>
            <tr className="bd-ligthgray">
              <th>이름 / 가입나이 / 성별</th>
              <th>주계약</th>
              <th>납입기간</th>
              <th>주계약 가입금액</th>
              <th>보험료</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td className="text-center">
                {mainPlanInfo.insuredName} / {mainPlanInfo.age} /{' '}
                {mainPlanInfo.genderLabel}
              </td>
              <td className="text-center">{mainPlanInfo.productFullName}</td>
              <td className="text-center">{mainPlanInfo.payinPeriodLabel}</td>
              <td className="text-center">
                {mainPlanInfo.faceAmount}
                {isUSD ? '달러' : '원'}
              </td>
              <td className="text-center">
                {makePremiumStandard(totalPremium)}
                {isUSD ? '달러' : '원'}
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  };

  const SearchOption = props => {
    const {
      additionalPaymentInfo: {
        startAddAge,
        endAddAge,
        periodType,
        monthlyPremium,
        annuityType,
        annuityTypeValue,
        annuityOpenAge,
        isUSD,
      },
    } = props;
    console.log(simulationList);
    return (
      <>
        <table className="tbl-sty6">
          <colgroup>
            <col style={{ width: '20rem' }} />
            <col style={{ width: '32rem' }} />
            <col style={{ width: '17rem' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <td>
                <span className="tit-small">추가납입보험료 납입계획</span>
              </td>
              <td colSpan="3">
                <table>
                  <colgroup>
                    <col style={{ width: '11rem' }} />
                    <col style={{ width: '2rem' }} />
                    <col style={{ width: '12.4rem' }} />
                    <col style={{ width: '10.8rem' }} />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td>
                        <SatSelect
                          value={startAddAge}
                          options={getStepOptions('startAdd')}
                          customStyleClass="w-100"
                          onChange={value =>
                            changeSimulationCondition(
                              'startAddAge',
                              value.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <span className="bold">~</span>
                      </td>
                      <td>
                        <SatSelect
                          value={endAddAge}
                          options={getStepOptions('endAdd')}
                          customStyleClass="w-100"
                          onChange={value =>
                            changeSimulationCondition('endAddAge', value.value)
                          }
                        />
                      </td>
                      <td>
                        <SatSelect
                          value={periodType}
                          options={getPeriodType()}
                          customStyleClass="w-100"
                          onChange={value =>
                            changeSimulationCondition('periodType', value.value)
                          }
                        />
                      </td>
                      <td>
                        <div className="amount w-154">
                          <SatInput
                            ref={inputRef}
                            type={'currency'}
                            currencyType={isUSD ? 'D' : 'W'}
                            value={monthlyPremium}
                            dataIndex={'premium'}
                            useRecommendBtn={'premium'}
                            outerSuffix={() => (
                              <span className="txt">
                                {isUSD ? '달러' : '원'}
                              </span>
                            )}
                            onChange={value =>
                              changeSimulationCondition('monthlyPremium', value)
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td>
                <span className="tit-small">사망보험금 연금선지급 서비스</span>
              </td>
              <td>
                <table>
                  <colgroup>
                    <col style={{ width: '21rem' }} />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td>
                        <SatSelect
                          value={annuityType}
                          customStyleClass="w-200"
                          options={[
                            { label: '선택안함', value: '0' },
                            {
                              label: '연금선지급기간 선택형',
                              value: '1',
                            },
                            { label: '연금선지급금 선택형', value: '2' },
                          ]}
                          onChange={value =>
                            changeSimulationCondition(
                              'annuityType',
                              value.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <SatSelect
                          disabled={annuityDisabled}
                          value={annuityTypeValue}
                          options={annuityTypeValueOptions}
                          customStyleClass="w-100"
                          onChange={value =>
                            changeSimulationCondition(
                              'annuityTypeValue',
                              value.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td>
                <span className="tit-small">연금선지급개시나이</span>
              </td>
              <td>
                <SatSelect
                  value={annuityOpenAge}
                  disabled={annuityDisabled}
                  options={getStepOptions('annuityOpenAge')}
                  customStyleClass="w-100"
                  onChange={value =>
                    changeSimulationCondition('annuityOpenAge', value.value)
                  }
                  title="연금선지급개시나이"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  };

  const getGuides = () => {
    const guides = [
      '일정 기간 동안 정기적으로 추가납입을 원하실 경우 납입할 기간 및 금액을 입력한 후 [계산]을 진행해 주세요.',
      '비정기 추가납입이 예상될 경우 하단 표의 해당 경과년수에 납입금액을 입력한 후 [계산]을 진행해 주세요.',
      `예시표의 경과년도 1년에 해당하는 내용은 ${
        +mainPlanInfo.age + 1
      }세 계약해당일 직전을 말하며, 납입보험료 및 해약환급금 등은 ${
        mainPlanInfo.age
      }세 말 기준금액입니다. 그 이후 경과년도도 동일한 기준입니다.`,
      '비정기 추가납입 시점은 해당 경과년도의 시작일입니다.(예: 경과년수 2년에 비정기납입하는 경우 해당 금액은 13회차 기본보험료 납입 시 함께 납입)',
    ];
    return guides;
  };

  console.log('mainPlanInfo', mainPlanInfo);
  return (
    <React.Fragment>
      <div className="additional-payment">
        <div className="additional-payment-header">
          <MainPlanInfo
            mainPlanInfo={mainPlanInfo}
            isUSD={additionalPaymentInfo.isUSD}
          />

          <SearchOption additionalPaymentInfo={additionalPaymentInfo} />
        </div>

        <GridGuide guides={getGuides()}></GridGuide>

        <div className="additional-payment-content">
          <AdditionalPaymentGrid
            additionalPaymentInfo={additionalPaymentInfo}
            calculationContent={calculationContent}
            calcData={
              simulationList.find(item => item.simlNm === type)?.calcData
            }
            isSummary={isSummary}
            setIsSummary={setIsSummary}
            irregularValueMap={irregularValueMap}
            setIrregularValueMap={setIrregularValueMap}
          />
        </div>
      </div>

      <SatModal
        visible={isValidCheck}
        setVisible={() => setIsValidCheck(false)}
        wide
      >
        <header className="modal-header">
          <h3 className="layer-tit2">시뮬레이션 불가 안내</h3>
        </header>
        <div className="cont">
          <div className="modal-scroll-wrap">
            <div className="info-box mgb-0">
              <div className="tit-small">참고사항</div>
              <p
                className="text-medium mgt-8"
                style={{ whiteSpace: 'pre-line' }}
              >
                {validMsg}
              </p>
              {/* 참고사항 */}
            </div>
          </div>
        </div>
      </SatModal>
    </React.Fragment>
  );
});

const AdditionalPaymentGrid = props => {
  const {
    calcData = {},
    additionalPaymentInfo,
    calculationContent,
    isSummary,
    setIsSummary,
    irregularValueMap,
    setIrregularValueMap,
  } = props;
  if (isEmptyObject(calcData)) return <></>;

  const [gridOptions, setGridOptions] = useState(null);
  const [invtErnRtList, setInvtErnRtList] = useState([]); // 수익률 리스트
  const [invtErnRtIdx, setInvtErnRtIdx] = useState(0); // 선택한 수익률 index

  const changeGridOptions = () => {
    const type = String(+invtErnRtIdx + 1);

    const data = calcData?.periodicDeathBenefitsDTO
      ?.map(item => ({
        ...item.additionalPaymentResponseDTO,
        insrnAge: item.insrnAge,
      }))
      .map(item => ({
        ...item,
        progPdVal:
          item.progPd / 12 < 1 ? `${item.progPd}개월` : `${item.progPd / 12}년`,
      }))
      .filter(item => !item.progPdVal?.includes('개월'))
      .filter(item =>
        isSummary
          ? parseInt(item.progPdVal?.replace(/\D/g, '')) % 5 === 0
          : true,
      ) // 정기납입금액 설정
      .map(item => {
        const startAge =
          +additionalPaymentInfo.applyAge + +additionalPaymentInfo.startAddAge;
        const insrnAge = +item.insrnAge;
        const addPaymentYear = +additionalPaymentInfo.endAddAge || 0; // 추가 납입 하는 년수
        let regularMonthly = '';
        // 정기 추가납입 기간 내
        if (insrnAge > startAge && insrnAge <= startAge + addPaymentYear) {
          regularMonthly =
            additionalPaymentInfo.monthlyPremium *
            (additionalPaymentInfo.periodType === 'month' ? 12 : 1);
        }
        return {
          ...item,
          regularMonthly,
        };
      });
    const gridOptions = {
      data,
      colNames: [
        '경과년수',
        '도달나이',
        '정기',
        '비정기',
        '납입보험료누계',
        '사망보장금액',
        '연금지급액',
        '주계약보험료 재원',
        '추가납입보험료 재원',
        '합계',
        '환급률',
      ],
      colModel: [
        {
          name: 'progPdVal',
          width: 115,
          rowspan: 2,
          align: 'right',
          sortable: false,
        },
        {
          name: 'insrnAge',
          width: 115,
          rowspan: 2,
          align: 'right',
          sortable: false,
          formatter: v => `${v}세`,
        },
        {
          name: 'regularMonthly',
          align: 'right',
          sortable: false,
          formatter: v => toCurrency(v),
        },
        {
          name: 'irregular',
          align: 'right',
          sortable: false,
          component: (row, index) => {
            const rowId = row.progPdVal.replace(/\D/g, '');
            // 1년은 비활성화
            if (rowId === '1') {
              return <></>;
            }
            return (
              <GridInput
                type="currency"
                currencyType="W"
                index={index}
                value={irregularValueMap[rowId]}
                onChange={(value, index) =>
                  handleInputChange(value, index, rowId)
                }
              />
            );
          },
        },
        {
          name: `payinPrmum${type === '1' ? '' : type}`,
          rowspan: 2,
          align: 'right',
          sortable: false,
          formatter: 'integer',
        },
        {
          name: `deathBnf${type}`,
          rowspan: 2,
          sortable: false,
          align: 'right',
          formatter: 'integer',
        },
        {
          name: `anutyPayAmnt${type}`,
          width: 135,
          rowspan: 2,
          align: 'right',
          sortable: false,
          formatter: 'integer',
        },
        { name: `mnpoliPrmum${type}`, align: 'right', formatter: 'integer' },
        { name: `addPayinPrmum${type}`, align: 'right', formatter: 'integer' },
        { name: `sumAmnt${type}`, align: 'right', formatter: 'integer' },
        {
          name: `refndRt${type}`,
          width: 100,
          rowspan: 2,
          align: 'right',
          sortable: false,
          formatter: v => `${(v * 100).toFixed(1)}%`,
        },
      ],
      multiHeaders: [
        [
          {
            startColumnName: 'regularMonthly',
            numberOfColumns: 2,
            titleText: '추가납입보험료',
          },
          {
            startColumnName: `mnpoliPrmum${type}`,
            numberOfColumns: 3,
            titleText: '주계약 해약환급금',
          },
        ],
      ],
      formatter: {
        integer: {
          thousnadsSeparator: ',',
          defaultValue: 0,
        },
      },
    };

    setGridOptions(gridOptions);
  };

  const handleInputChange = (value, index, rowId) => {
    console.log(
      'handleInputChange() - value, index, rowId',
      value,
      index,
      rowId,
    );
    const newIrregularValueMap = { ...irregularValueMap };
    newIrregularValueMap[rowId] = value;
    setIrregularValueMap(newIrregularValueMap);
  };

  useEffect(changeGridOptions, [
    invtErnRtList,
    invtErnRtIdx,
    isSummary,
    additionalPaymentInfo,
    calcData,
  ]);

  useEffect(() => {
    // 수익률 리스트 설정
    const jpsRateInfoDTO = calculationContent?.jpsRateInfoDTO;
    if (jpsRateInfoDTO) {
      setInvtErnRtList(
        [
          jpsRateInfoDTO.invtErnRt1,
          jpsRateInfoDTO.invtErnRt2,
          jpsRateInfoDTO.invtErnRt3,
        ].filter(o => o),
      );
    }
  }, []);

  return (
    <>
      <GridHeader title="보험료 추가납입 시 예시">
        <SatCheckBox
          value={[isSummary]}
          options={[{ value: false, label: '경과년수 요약보기' }]}
          onChange={value => setIsSummary(value)}
        />
        <SatRadio
          name={'invtErnRt'}
          value={invtErnRtIdx}
          options={(invtErnRtList || []).map((item, index) => ({
            label: `${item.toFixed(3)}%`,
            value: index,
          }))}
          onChange={option => {
            setInvtErnRtIdx(option.value);
          }}
        />
      </GridHeader>

      {gridOptions && (
        <GridTable id="additionalPaymentGrid" options={gridOptions} />
      )}
    </>
  );
};

export default React.memo(AdditionalPayment);
